import { Star, MessageSquare, Share } from 'lucide-react';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { useState, useEffect } from 'react';

import { config, callApi } from './NetUtils';
import ReportItem from './ReportItem';
import { removeUserReport, getUserReportSecret } from './reportStorageUtils';

const CompanyProfile = ({ company, reports, lastEvaluatedKey, loadingMore, fetchMore, fetchCompanyData }) => {
  const handleShare = async () => {
    const shareData = {
      title: `AI Transparency Report for ${company.companyName}`,
      text: `Check out the AI usage reports and score for ${company.companyName} on AI Transparency.`,
      url: window.location.href,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        alert('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing:', error);
      if (error.name !== 'AbortError') {
        alert('Could not open share dialog.');
      }
    }
  };

  const handleDeleteReport = async (reportId, companyName) => {
    const reportSecret = getUserReportSecret(reportId);
    if (!reportSecret) {
      alert('Error: Report secret not found. Cannot delete.');
      return;
    }

    const response = await callApi(
      config.DATA_API_URL,
      `/reports/${encodeURIComponent(companyName)}/${encodeURIComponent(reportId)}?secret=${encodeURIComponent(reportSecret)}`,
      'DELETE'
    );

    if (response.success) {
      removeUserReport(reportId);
      fetchCompanyData();
    } else {
      alert('Failed to delete report: ' + response.error);
    }
  };

  return (
    <PullToRefresh onRefresh={() => fetchCompanyData()}>
      <div className="max-w-4xl mx-auto space-y-8">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg border dark:border-gray-700">
          <div className="flex justify-between items-start mb-2">
            <h2 className="text-4xl font-extrabold">{company.companyName}</h2>
            <button onClick={handleShare} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              <Share className="w-6 h-6 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
          <div className="flex items-center flex-wrap gap-x-6 gap-y-2">
            <div className="flex items-center space-x-2 text-yellow-500">
              <Star className="w-6 h-6" /><span className="font-bold text-xl">Score: {company.rating}</span>
            </div>
            <div className="flex items-center space-x-2">
              <MessageSquare className="w-6 h-6" /><span className="font-bold text-xl whitespace-nowrap">{company.reportCount} Reports</span>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg border dark:border-gray-700">
          <h3 className="text-2xl font-bold mb-6">Anonymous Reports</h3>
          {reports.length > 0 ? (
            <div className="space-y-6">
              {reports.map(r => <ReportItem key={r.reportId} report={r} companyName={company.companyName} onDelete={handleDeleteReport} />)}
              {lastEvaluatedKey && (
                <div className="text-center pt-4">
                  <button onClick={fetchMore} disabled={loadingMore} className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:bg-blue-400">
                    {loadingMore ? 'Loading...' : 'Load More'}
                  </button>
                </div>
              )}
            </div>
          ) : <p className="text-center py-8">No reports found for this company.</p>}
        </div>
      </div>
    </PullToRefresh>
  );
};

export default CompanyProfile;
